import { useEffect, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';

import {
  ENROLL_QUEUE_PANEL_MAX_WIDTH_FRACTION,
  ENROLL_QUEUE_PANEL_MIN_WIDTH_PX,
  defaultPanelWidthPx,
  reclampPanelWidthToViewport,
  resetPanelWidth,
  setPanelWidthPx,
  useEnrollQueuePanelWidthStore,
} from '../../EnrollQueue/panel-width-store';
import { PanelResizeHandle } from '../ui/PanelResizeHandle';

/**
 * Left-edge drag handle for the V2 enroll-queue rail (plan 2026-09-30-002,
 * D7 + D14).
 *
 * D7: the rail is resizable on EVERY tab, Chat included — this handle mounts
 * unconditionally while the dock is open, so there is no per-tab gate to keep
 * in sync (the prototype's `pushWidth !== 'var'` gate is clayground's problem,
 * not Terra's).
 *
 * Drag/keyboard/clamp mechanics are DELEGATED to `shell-next/ui/PanelResizeHandle`
 * (`orientation="vertical"`, `side="after"` — the rail sits AFTER its handle in
 * flow: right-anchored, strip on its LEFT edge, so a FALLING clientX /
 * `ArrowLeft` widens it, the inverse of `PanelResizeHandle`'s default
 * `side="before"`, which is what `panels/FlowPanels.tsx`'s Actions divider
 * uses too). `PanelResizeHandle` also owns double-click-to-reset (`onReset`),
 * the drag-time `<body>` cursor/select-none classes and the jsdom-safe
 * optional pointer-capture calls — this component does none of them by hand.
 *
 * What stays RAIL-LOCAL:
 *  - the store glue: the width itself lives in `EnrollQueue/panel-width-store.ts`
 *    and is shared with `QueueContentShift`, the V1 sidebar and
 *    `GongFlowBrowse` — this component only ever calls its three writers
 *    (`setPanelWidthPx`, `resetPanelWidth`, `reclampPanelWidthToViewport`) and
 *    never clamps on its own; `PanelResizeHandle`'s own internal `min`/`max`
 *    clamp runs too, but lands on the exact same bounds, so it's a harmless
 *    no-op second clamp, not a second source of truth.
 *  - the reset TARGET for D14: `PanelResizeHandle` fires `onReset` on
 *    double-click; what it resets to (`resetPanelWidth` → store `null` → the
 *    35vw CSS default) is this store's business, not the handle's.
 *  - the window-resize reclamp effect, and the `viewport` state it mirrors
 *    into render (so `aria-valuemax`, computed fresh every render from this
 *    state and passed into `PanelResizeHandle` as `max`, re-renders on
 *    resize even in the `widthPx === null` case, where the store itself
 *    never changes).
 *  - `data-flow-rail-resize-handle` and the pointerdown `stopPropagation`,
 *    both on the OUTER 5px-wide positioned wrapper div — `PanelResizeHandle`'s
 *    own root (which fills that wrapper via `inset-0`) is the actual
 *    `role="separator"` element and the one carrying the drag/keyboard
 *    handlers.
 */

function viewportWidthPx(): number {
  return typeof window === 'undefined' ? 0 : window.innerWidth;
}

export interface RailResizeHandleProps {
  /**
   * Formerly read at pointerdown time to resume a drag from the panel's
   * MEASURED width when the store was still `null` (nobody had dragged yet).
   * `PanelResizeHandle`'s `value` is a controlled prop, so there is no
   * pointerdown-time measurement step left to resume from:
   * `defaultPanelWidthPx()` (what `value` resolves to below when `widthPx`
   * is `null`) IS the un-dragged panel's rendered width (`round(35vw)`), so
   * there is nothing the old measurement recovered that the controlled value
   * doesn't already carry. Kept (unused) only so existing callers — including
   * this component's own test suite — don't need to drop the prop.
   */
  panelRef: RefObject<HTMLElement | null>;
}

export function RailResizeHandle({ panelRef }: RailResizeHandleProps) {
  void panelRef;

  const widthPx = useEnrollQueuePanelWidthStore(s => s.widthPx);
  // Mirrored into state (rather than read inline at render) so `aria-valuemax`
  // re-renders when the window resizes even in the `widthPx === null` case,
  // where the store never changes and would not re-render us on its own.
  const [viewport, setViewport] = useState(viewportWidthPx);

  // The value a screen reader / a keyboard nudge starts from: the dragged
  // width when there is one, otherwise the `35vw` default resolved to px.
  const effectiveWidthPx = widthPx ?? defaultPanelWidthPx();

  // StrictMode-safe by construction: no lifetime ref to reset, because the
  // listener identity is created fresh inside the effect and removed by the
  // matching cleanup (docs/v4-system/strictmode-resilient-effects.md).
  useEffect(() => {
    const onResize = () => {
      reclampPanelWidthToViewport();
      setViewport(viewportWidthPx());
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
    <div
      data-flow-rail-resize-handle
      className="absolute inset-y-0 left-0 z-20 w-[5px] shrink-0"
      onPointerDown={handlePointerDown}
    >
      <PanelResizeHandle
        orientation="vertical"
        side="after"
        label="Resize dock"
        value={effectiveWidthPx}
        onValueChange={setPanelWidthPx}
        onReset={resetPanelWidth}
        min={ENROLL_QUEUE_PANEL_MIN_WIDTH_PX}
        max={Math.round(viewport * ENROLL_QUEUE_PANEL_MAX_WIDTH_FRACTION)}
      />
    </div>
  );
}
